import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslations } from 'use-intl';
import type { BodyFormat, LinkPreviewView, MentionView } from '@leanin/shared';
import type { ReactionKind } from '@leanin/shared/reactions';
import {
  AlertDialog,
  Avatar,
  Body,
  Button,
  Caption,
  DotSep,
  DropdownMenu,
  DropdownMenuItem,
  I,
  Label,
  Popover,
  Pressable,
  Row,
  Stack,
} from '@leanin/ui';
import { normalizeLegacyBody, PostBodyRenderer } from '../editor';
import type { RichTextEditorHandle } from '../editor/RichTextEditor';
import { CommentComposer } from '../feed/CommentComposer';
import type { MentionAllGroup } from '../feed/MentionPopover';
import { LinkPreviewCard } from '@/components/link-preview';
import { ReactionsModal } from '../feed/ReactionsModal';
import type { LinkPreviewInput } from '../../lib/mutations/feed';
import type { MemberView } from '../../lib/queries/identity';
import { ReportContentDialog } from '../moderation/ReportContentDialog';
import {
  DEFAULT_REACTION,
  REACTION_BY_ID,
  REACTION_LABEL_KEY,
  ReactionPicker,
  ReactionStack,
} from '../posts/index';
import { useFormatDate } from '../../lib/format-date.client';
import { resolveShareUrl } from '../../lib/share-url';
import { LoadMore } from '../pagination/index';
import { RouterLinkAs } from '../../lib/router-link';
import { memberPath } from '../../lib/routes';

// Entity-agnostic comments panel — list (oldest→newest), inline author
// edit, author delete-with-confirm, non-author reporting (opt-in via
// `reportKind`, for surfaces whose comments are a moderation target), and
// the composer row. Posts and resources wrap it with their own
// query/mutation wiring (PostComments, ResourceComments); the panel itself
// never touches react-query — data-source wiring arrives through props
// (including the `replies.useThread` hook for one-level reply threads).
//
// Everything beyond the base list is OPT-IN via props: per-comment reactions
// (`onReact`), deep links (`commentHref`), reply threads (`replies`), and
// deep-link highlighting (`highlight`). Resources pass none of them and keep
// the original flat panel.
//
// Strings live under the Feed.comments namespace: they are surface-neutral
// ("Add a comment…", "Delete comment?") and forking them per entity would
// drift the copy.

export type CommentItemView = {
  readonly id: string;
  readonly authorUserId: string;
  readonly authorName: string | null;
  readonly authorPhotoUrl: string | null;
  readonly body: string;
  readonly bodyFormat: BodyFormat;
  readonly mentions: ReadonlyArray<MentionView>;
  readonly linkPreviews: ReadonlyArray<LinkPreviewView>;
  readonly createdAt: string;
  readonly editedAt: string | null;
  // Engagement fields — present only on surfaces whose comments carry
  // reactions/replies (posts). Resources omit them and the affordances stay
  // hidden.
  readonly reactionCounts?: Record<ReactionKind, number>;
  readonly viewerReaction?: ReactionKind | null;
  readonly replyCount?: number;
  readonly parentCommentId?: string | null;
};

export type CommentErrorKind =
  | 'loadFailed'
  | 'postFailed'
  | 'saveFailed'
  | 'deleteFailed'
  | 'reactFailed'
  | 'repliesFailed';

export type CommentReactionResult = {
  reactionCounts: Record<ReactionKind, number>;
  viewerReaction: ReactionKind | null;
};

export type ReplyThreadData = {
  items: ReadonlyArray<CommentItemView>;
  hasMore: boolean;
  isFetching: boolean;
  error: unknown;
  fetchMore: () => void;
  /** Imperative refetch of the loaded pages, independent of staleness. Used
   *  when expanding a seeded thread whose cache may hold only viewer-created
   *  replies appended while collapsed (a synthetic page with no cursor). */
  refetch: () => void;
};

export type CommentRepliesConfig = {
  /** Custom hook wiring one thread's replies (posts: react-query infinite
   *  query). Called unconditionally by an always-mounted per-thread
   *  component, so the rules of hooks hold as long as the `replies` prop is
   *  present for the panel's whole lifetime — pass it statically, never
   *  conditionally. */
  useThread: (
    parentCommentId: string,
    opts: { enabled: boolean },
  ) => ReplyThreadData;
  /** Persists a reply into the thread's data source; the created reply must
   *  surface through `useThread` (posts append to the react-query cache). */
  onCreateReply: (parentCommentId: string, body: string) => Promise<void>;
  /** Resolves the @-mention prefill used when replying to a reply. Resolving
   *  to null (or omitting) opens the composer empty. */
  mentionForAuthor?: (comment: CommentItemView) => Promise<MentionView | null>;
  /** Each thread's oldest reply keyed by parent comment id (posts: zipped
   *  from the listing's `firstReplies`). A seeded thread renders its first
   *  reply immediately with a "View N more replies" affordance; threads
   *  without an entry keep the collapsed "View N replies" behavior. */
  firstReplies?: ReadonlyMap<string, CommentItemView>;
};

// One reply thread's in-progress composer state. Kept per thread so switching
// which comment you reply to preserves each draft (and its seeded @-mentions)
// instead of discarding it.
type ReplyDraftState = {
  readonly draft: string;
  readonly mentions: ReadonlyArray<MentionView>;
};

type PanelProps = {
  /** Stable id suffix for the composer element (`comment-<targetId>`). */
  targetId: string;
  items: ReadonlyArray<CommentItemView>;
  isLoading: boolean;
  loadError: unknown;
  hasEarlier: boolean;
  isFetchingEarlier: boolean;
  onFetchEarlier: () => void;
  viewerUserId: string | null;
  /** Resolves after the comment is persisted; the panel clears the draft. */
  onCreate: (
    body: string,
    linkPreviews: ReadonlyArray<LinkPreviewInput>,
  ) => Promise<void>;
  /** Unfurls a pasted https URL into a link-preview card. Never rejects and
   *  never returns null — the caller degrades to a minimal domain-only card
   *  (matching the post composer). Omit for surfaces whose comments don't
   *  store link previews (resources); pasted URLs then stay autolinked text. */
  resolveLinkPreview?: (url: string) => Promise<LinkPreviewInput>;
  /** Resolves with the persisted body/mentions for optimistic display. */
  onUpdate: (
    commentId: string,
    body: string,
  ) => Promise<{
    body: string;
    bodyFormat: BodyFormat;
    mentions: ReadonlyArray<MentionView>;
  }>;
  onDelete: (commentId: string) => Promise<void>;
  /** Maps a failed request onto user-facing copy. */
  formatError: (err: unknown, kind: CommentErrorKind) => string;
  onCommentAdded?: (() => void) | undefined;
  /** Scope @-mention suggestions; only meaningful with mentions enabled. */
  audienceGroupId?: string | undefined;
  /** Member pinned to the top of the bare-"@" suggestion list in every
   *  composer on this surface (the post author). Only meaningful with
   *  mentions enabled. */
  mentionPriorityMember?: MemberView | undefined;
  /** Off for surfaces whose comment bodies aren't mention-resolved
   *  server-side (resources). */
  mentionsEnabled?: boolean;
  /** Set when the viewer may address this surface's whole group with `@all`
   *  — the composers then offer the synthetic "Everyone in {group}" row. Its
   *  presence IS the permission: a caller that may not @all omits it. The
   *  server re-decides on write. */
  mentionAllGroup?: MentionAllGroup | undefined;
  /** Set when the surface's comments are a moderation report target (posts).
   *  Non-authors then get an overflow menu with a Report entry. Omit for
   *  surfaces without a target kind (resources). */
  reportKind?: 'comment';
  /** Off for viewers who can read the thread but may not add to it — a
   *  visitor on an open-content group's feed. Hides the composer row and the
   *  Reply affordance; the list stays readable. */
  composerEnabled?: boolean;
  /** Per-comment reaction toggle (posts). Resolves with the authoritative
   *  counts; rejects roll the optimistic update back. Pass only for viewers
   *  who may react. */
  onReact?: (
    commentId: string,
    kind: ReactionKind,
  ) => Promise<CommentReactionResult>;
  /** Deep-link URL for one comment — enables "Copy link to comment" in the
   *  overflow menu. Omit for surfaces without comment deep links
   *  (resources). */
  commentHref?: (commentId: string) => string;
  /** One-level reply threads (posts). */
  replies?: CommentRepliesConfig;
  /** Deep-link target: scroll to + flash this comment once rendered,
   *  auto-expanding its reply thread first when the target is a reply. */
  highlight?: { commentId: string; parentCommentId: string | null };
};

// Tight padding for the Like/Reply text actions under a bubble — the default
// Button size="sm" padding reads as gaps between the meta-row items.
const metaActionPadding = {
  padding: 'var(--space-none) var(--space-xs)',
} as const;

// Thread affordances ("View N replies") are text actions, not chips — the
// sm control height floats them away from the reply above on mobile.
const threadActionStyle = {
  ...metaActionPadding,
  height: 'auto',
} as const;

const ZERO_REACTION_COUNTS: Record<ReactionKind, number> = {
  support: 0,
  helpful: 0,
  insightful: 0,
  same: 0,
};

const EMPTY_REPLY_DRAFT: ReplyDraftState = { draft: '', mentions: [] };

function CommentItem({
  comment: c,
  viewerUserId,
  onUpdate,
  onDelete,
  formatError,
  audienceGroupId,
  mentionPriorityMember,
  mentionsEnabled,
  mentionAllGroup,
  reportKind,
  onReact,
  commentHref,
  onReplyClick,
  highlighted = false,
}: {
  comment: CommentItemView;
  viewerUserId: string | null;
  onUpdate: PanelProps['onUpdate'];
  onDelete: PanelProps['onDelete'];
  formatError: PanelProps['formatError'];
  audienceGroupId: string | undefined;
  mentionPriorityMember: MemberView | undefined;
  mentionsEnabled: boolean;
  mentionAllGroup: MentionAllGroup | undefined;
  reportKind: PanelProps['reportKind'];
  onReact: PanelProps['onReact'];
  commentHref: PanelProps['commentHref'];
  /** Opens the thread's shared reply composer; absent → no Reply action. */
  onReplyClick?: () => void;
  highlighted?: boolean;
}) {
  const t = useTranslations('Feed');
  const fmt = useFormatDate();
  const [editing, setEditing] = useState(false);
  const [editDraft, setEditDraft] = useState('');
  const [editError, setEditError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [deleted, setDeleted] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [displayBody, setDisplayBody] = useState(c.body);
  const [displayBodyFormat, setDisplayBodyFormat] = useState(c.bodyFormat);
  const [displayMentions, setDisplayMentions] = useState(c.mentions);

  // Reaction display state, seeded from the cached comment on mount — same
  // local-state pattern PostCard uses for the post-level reactions.
  const [reactionCounts, setReactionCounts] = useState<
    Record<ReactionKind, number>
  >(c.reactionCounts ?? ZERO_REACTION_COUNTS);
  const [viewerReaction, setViewerReaction] = useState<ReactionKind | null>(
    c.viewerReaction ?? null,
  );
  const [reactError, setReactError] = useState<string | null>(null);
  const [reactionsOpen, setReactionsOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const pickerHoverTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [copied, setCopied] = useState(false);
  const copiedTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (pickerHoverTimeout.current !== null) {
        clearTimeout(pickerHoverTimeout.current);
      }
      if (copiedTimeout.current !== null) clearTimeout(copiedTimeout.current);
    },
    [],
  );
  const openPicker = () => {
    if (pickerHoverTimeout.current !== null) {
      clearTimeout(pickerHoverTimeout.current);
      pickerHoverTimeout.current = null;
    }
    setPickerOpen(true);
  };
  const schedulePicker = () => {
    pickerHoverTimeout.current = setTimeout(() => setPickerOpen(false), 160);
  };

  // Deep-link target: scroll into view once rendered, flash, fade.
  const itemRef = useRef<HTMLDivElement | null>(null);
  const [flash, setFlash] = useState(false);
  useEffect(() => {
    if (!highlighted) return;
    itemRef.current?.scrollIntoView({ block: 'center' });
    setFlash(true);
    const timer = setTimeout(() => setFlash(false), 1800);
    return () => clearTimeout(timer);
  }, [highlighted]);

  if (deleted) return null;

  const isAuthor = viewerUserId !== null && viewerUserId === c.authorUserId;
  const reactionTotal =
    reactionCounts.support +
    reactionCounts.helpful +
    reactionCounts.insightful +
    reactionCounts.same;

  const onSaveEdit = async () => {
    const trimmed = editDraft.trim();
    if (trimmed.length === 0) {
      setEditError(t('comments.editEmpty'));
      return;
    }
    setEditError(null);
    setSaving(true);
    try {
      const updated = await onUpdate(c.id, trimmed);
      setDisplayBody(updated.body);
      setDisplayBodyFormat(updated.bodyFormat);
      setDisplayMentions(updated.mentions);
      setEditing(false);
    } catch (err) {
      setEditError(formatError(err, 'saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const onConfirmDelete = async () => {
    setDeleteOpen(false);
    setDeleteError(null);
    try {
      await onDelete(c.id);
      setDeleted(true);
    } catch (err) {
      setDeleteError(formatError(err, 'deleteFailed'));
    }
  };

  const onReactPick = (kind: ReactionKind) => {
    if (onReact === undefined) return;
    setReactError(null);
    const previousCounts = reactionCounts;
    const previousViewerReaction = viewerReaction;
    const next: ReactionKind | null =
      previousViewerReaction === kind ? null : kind;
    const optimistic: Record<ReactionKind, number> = { ...previousCounts };
    if (previousViewerReaction !== null) {
      optimistic[previousViewerReaction] = Math.max(
        0,
        optimistic[previousViewerReaction] - 1,
      );
    }
    if (next !== null) optimistic[next] = optimistic[next] + 1;
    setReactionCounts(optimistic);
    setViewerReaction(next);
    onReact(c.id, kind).then(
      (result) => {
        setReactionCounts(result.reactionCounts);
        setViewerReaction(result.viewerReaction);
      },
      (err: unknown) => {
        setReactionCounts(previousCounts);
        setViewerReaction(previousViewerReaction);
        setReactError(formatError(err, 'reactFailed'));
      },
    );
  };

  const onCopyLink = () => {
    if (commentHref === undefined) return;
    if (typeof navigator === 'undefined' || navigator.clipboard === undefined) {
      return;
    }
    void navigator.clipboard.writeText(resolveShareUrl(commentHref(c.id))).then(
      () => {
        setCopied(true);
        if (copiedTimeout.current !== null) clearTimeout(copiedTimeout.current);
        copiedTimeout.current = setTimeout(() => setCopied(false), 2000);
      },
      () => {
        // clipboard permission denied — silent; matches CopyLinkButton
      },
    );
  };

  const showOverflow =
    commentHref !== undefined || isAuthor || reportKind !== undefined;
  const overflow = showOverflow ? (
    <DropdownMenu
      trigger={<Button variant="ghost" size="sm" icon={I.more} aria-label={t('comments.moreActionsAria')} />}
    >
      {commentHref !== undefined ? (
        <DropdownMenuItem onSelect={onCopyLink}>
          {copied ? t('comments.menu.copied') : t('comments.menu.copyLink')}
        </DropdownMenuItem>
      ) : null}
      {isAuthor ? (
        <DropdownMenuItem
          onSelect={() => {
            // The editor speaks the v2 dialect; a v1 body is hydrated through
            // the normalizer (same as the post composer) and saves back as v2.
            setEditDraft(
              displayBodyFormat === 'v1' ? normalizeLegacyBody(displayBody) : displayBody,
            );
            setEditError(null);
            setEditing(true);
          }}
        >
          {t('comments.menu.edit')}
        </DropdownMenuItem>
      ) : null}
      {isAuthor ? (
        <DropdownMenuItem destructive onSelect={() => setDeleteOpen(true)}>
          {t('comments.menu.delete')}
        </DropdownMenuItem>
      ) : null}
      {!isAuthor && reportKind !== undefined ? (
        <DropdownMenuItem onSelect={() => setReportOpen(true)}>
          {t('comments.menu.report')}
        </DropdownMenuItem>
      ) : null}
    </DropdownMenu>
  ) : undefined;

  // Bottom-right corner count pill — the who-reacted trigger. Shown whenever
  // the surface carries reaction data, independent of whether the viewer may
  // react (the roster is read-gated only).
  const reactionPill =
    c.reactionCounts !== undefined && reactionTotal > 0 ? (
      <Pressable
        onClick={() => setReactionsOpen(true)}
        aria-label={t('reactions.seeWhoAria')}
        style={{
          position: 'absolute',
          // Rides past the bubble's corner (approved mockup: -8/-10 optical
          // offsets), lifted off the fill by a soft shadow — the one place
          // the design uses one, so no shadow token exists for it.
          insetInlineEnd: -8,
          bottom: -10,
          // eslint-disable-next-line @leanin/ui/no-raw-shadow-literals -- every --shadow-* token is `none`; this design-approved lift has no token to use
          boxShadow: '0 1px 4px rgba(0, 0, 0, 0.08)',
          display: 'inline-flex',
          alignItems: 'center',
          // eslint-disable-next-line @leanin/ui/no-inline-spacing-vars -- Pressable is not a layout primitive; no gap prop exists on it
          gap: 'var(--space-xs)',
          // 2px vertical is an optical fit around the 16px discs; the disc
          // side gets less inline padding than the number so both read
          // centered inside the pill (mirrors the approved mockup).
          // eslint-disable-next-line @leanin/ui/no-inline-spacing-vars -- asymmetric optical padding (2px vertical) is not expressible as SpaceKey props
          padding: '2px var(--space-sm) 2px var(--space-xs)',
          background: 'var(--card)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-full)',
          cursor: 'pointer',
        }}
      >
        <ReactionStack
          counts={reactionCounts}
          mine={viewerReaction}
          size={16}
          max={2}
        />
        <Label size="sm">{reactionTotal}</Label>
      </Pressable>
    ) : null;

  const likeLabel =
    viewerReaction !== null
      ? t(REACTION_LABEL_KEY[viewerReaction])
      : t('comments.like');
  const likeAction =
    onReact !== undefined ? (
      <Popover
        open={pickerOpen}
        onOpenChange={(open) => {
          if (!open) setPickerOpen(false);
        }}
        allowOverflow
        trigger={
          <span onMouseEnter={openPicker} onMouseLeave={schedulePicker}>
            <Button
              variant="ghost"
              size="sm"
              style={{
                ...metaActionPadding,
                ...(viewerReaction !== null
                  ? { color: REACTION_BY_ID[viewerReaction].ink }
                  : {}),
              }}
              onClick={() => {
                // Plain click toggles Love; a click while reacted removes the
                // current reaction (the picker handles switching kinds).
                onReactPick(viewerReaction ?? DEFAULT_REACTION);
                setPickerOpen(false);
              }}
            >
              {likeLabel}
            </Button>
          </span>
        }
        side="top"
        align="start"
      >
        <div onMouseEnter={openPicker} onMouseLeave={schedulePicker}>
          <ReactionPicker
            mine={viewerReaction}
            onPick={(kind) => {
              onReactPick(kind);
              setPickerOpen(false);
            }}
          />
        </div>
      </Popover>
    ) : null;

  const sublabel =
    c.editedAt !== null
      ? `${fmt.relativeShort(c.createdAt)} · ${t('comments.edited')}`
      : fmt.relativeShort(c.createdAt);

  const body = editing ? (
    <Stack gap="sm">
      <CommentComposer
        id={`comment-edit-${c.id}`}
        value={editDraft}
        onChange={setEditDraft}
        onSubmit={() => void onSaveEdit()}
        disabled={saving}
        mentionsEnabled={mentionsEnabled}
        initialMentions={displayMentions}
        previousBody={displayBody}
        ariaLabel={t('comments.editAria')}
        {...(audienceGroupId !== undefined ? { audienceGroupId } : {})}
        {...(mentionPriorityMember !== undefined
          ? { mentionPriorityMember }
          : {})}
        {...(mentionAllGroup !== undefined ? { mentionAllGroup } : {})}
      />
      {editError !== null ? (
        <Body size="sm" color="destructive">
          {editError}
        </Body>
      ) : null}
      <Row gap="sm" justify="flex-end">
        <Button
          variant="secondary"
          size="sm"
          type="button"
          onClick={() => {
            setEditing(false);
            setEditError(null);
          }}
          disabled={saving}
        >
          {t('comments.cancel')}
        </Button>
        <Button
          variant="primary"
          size="sm"
          type="button"
          onClick={() => void onSaveEdit()}
          disabled={editDraft.trim().length === 0}
          loading={saving}
        >
          {t('comments.save')}
        </Button>
      </Row>
    </Stack>
  ) : (
    <Stack gap="xs" align="flex-start">
      {/* Warm speech bubble around author + text, per the approved design.
          Width hugs the content; the count pill rides its corner. */}
      <div style={{ position: 'relative', maxWidth: '100%' }}>
        <Stack
          gap="none"
          px="md"
          py="sm"
          style={{
            // --muted (warm-200): light warm-brown bubble fill — poppy read
            // too red, --accent too dark next to the sidebar selection.
            background: 'var(--muted)',
            borderRadius:
              'var(--radius-sm) var(--radius-lg) var(--radius-lg) var(--radius-lg)',
            // The corner pill overhangs the bubble's bottom-right; widen the
            // bubble rightward (not downward) so text never runs under it.
            ...(reactionPill !== null
              ? { paddingInlineEnd: 'calc(var(--space-2xl) + var(--space-md))' }
              : {}),
          }}
        >
          <RouterLinkAs
            href={memberPath(c.authorUserId)}
            style={{ color: 'inherit', textDecoration: 'none', alignSelf: 'flex-start' }}
          >
            <Label size="sm">{c.authorName ?? t('comments.memberFallback')}</Label>
          </RouterLinkAs>
          <PostBodyRenderer
            bodyFormat={displayBodyFormat}
            mentions={displayMentions}
            style={{ margin: 0 }}
          >
            {displayBody}
          </PostBodyRenderer>
        </Stack>
        {reactionPill}
      </div>
      {c.linkPreviews.length > 0 ? (
        <div
          style={{
            display: 'grid',
            // eslint-disable-next-line @leanin/ui/no-inline-spacing-vars -- CSS grid layout; no Box prop for grid containers
            gap: 'var(--space-sm)',
            maxWidth: 480,
          }}
        >
          {c.linkPreviews.map((preview, idx) => (
            <LinkPreviewCard key={`${preview.url}:${idx}`} preview={preview} />
          ))}
        </div>
      ) : null}
      {/* baseline, not center: the buttons' 12px/1 label and the Caption's
          12px/1.4 line-box center ~1px apart; baseline puts the text on one
          line. DotSep keeps its own alignSelf: center. */}
      <Row gap="xs" align="baseline" pl="xs">
        {likeAction}
        {likeAction !== null && onReplyClick !== undefined ? <DotSep /> : null}
        {onReplyClick !== undefined ? (
          <Button
            variant="ghost"
            size="sm"
            style={metaActionPadding}
            onClick={onReplyClick}
          >
            {t('comments.reply')}
          </Button>
        ) : null}
        {likeAction !== null || onReplyClick !== undefined ? <DotSep /> : null}
        <Caption style={{ whiteSpace: 'nowrap' }}>{sublabel}</Caption>
      </Row>
      {reactError !== null ? (
        <span role="alert">
          <Body size="sm" color="destructive">
            {reactError}
          </Body>
        </span>
      ) : null}
      {deleteError !== null ? (
        <span role="alert">
          <Body size="sm" color="destructive">
            {deleteError}
          </Body>
        </span>
      ) : null}
    </Stack>
  );

  return (
    <>
      <div
        ref={itemRef}
        style={{
          borderRadius: 'var(--radius-md)',
          transition:
            'background-color var(--motion-slow) var(--motion-standard), outline-color var(--motion-slow) var(--motion-standard)',
          // Both states set the `outline` shorthand — flipping between the
          // shorthand and outlineColor makes React drop the longhand on
          // rerender (and warns).
          outline: flash
            ? '2px solid var(--primary)'
            : '2px solid transparent',
          ...(flash ? { backgroundColor: 'var(--accent)' } : {}),
        }}
      >
        <Row gap="sm" align="flex-start">
          <Avatar
            src={c.authorPhotoUrl ?? undefined}
            name={c.authorName ?? t('comments.memberFallback')}
            size={32}
            decorative
            href={memberPath(c.authorUserId)}
            linkAs={RouterLinkAs}
          />
          <Stack gap="none" style={{ flex: 1, minWidth: 0 }}>
            {body}
          </Stack>
          {overflow}
        </Row>
      </div>
      <AlertDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title={t('comments.deleteDialog.title')}
        description={t('comments.deleteDialog.description')}
        actionLabel={t('comments.deleteDialog.confirm')}
        cancelLabel={t('comments.deleteDialog.cancel')}
        destructive
        onAction={() => void onConfirmDelete()}
      />
      {reportKind !== undefined ? (
        <ReportContentDialog
          isOpen={reportOpen}
          onOpenChange={setReportOpen}
          targetKind={reportKind}
          targetId={c.id}
          {...(c.authorName !== null ? { subjectName: c.authorName } : {})}
        />
      ) : null}
      {reactionsOpen ? (
        <ReactionsModal
          target={{ kind: 'comment', id: c.id }}
          counts={reactionCounts}
          open={reactionsOpen}
          onOpenChange={setReactionsOpen}
        />
      ) : null}
    </>
  );
}

// One top-level comment's reply thread: indent + left rule, lazy-loaded via
// the host-supplied `useThread` hook, expandable, hosting the thread's shared
// reply composer when it owns it. Always mounted (hook rules) — renders null
// while there is nothing to show.
//
// A thread whose oldest reply arrived with the listing (`replies.firstReplies`)
// renders it immediately — the thread reads as open — with "View N more
// replies" starting the real per-thread query. Once its pages load, the ASC
// first item IS the seeded reply, so the seed is dropped on id-match rather
// than double-rendered.
function ReplyThread({
  parent,
  replies,
  highlight,
  composer,
  formatError,
  renderItem,
}: {
  parent: CommentItemView;
  replies: CommentRepliesConfig;
  highlight: PanelProps['highlight'];
  /** The panel's single reply composer when this thread owns it. */
  composer: ReactNode | null;
  formatError: PanelProps['formatError'];
  renderItem: (reply: CommentItemView) => ReactNode;
}) {
  const t = useTranslations('Feed');
  const replyCount = parent.replyCount ?? 0;
  const seed = replies.firstReplies?.get(parent.id);
  const seeded = seed !== undefined;
  const highlightInThread =
    highlight !== undefined && highlight.parentCommentId === parent.id;
  const [expanded, setExpanded] = useState(highlightInThread);
  const composerOpen = composer !== null;
  // The highlight's parentCommentId resolves from an async lookup, so this
  // thread can mount before the deep-link target is known — expand when it
  // arrives, not only at mount.
  useEffect(() => {
    if (highlightInThread) setExpanded(true);
  }, [highlightInThread]);

  const { useThread } = replies;
  const thread = useThread(parent.id, {
    enabled: expanded && replyCount > 0,
  });

  // Deep-linked reply: page the thread forward until the target is loaded.
  useEffect(() => {
    if (!highlightInThread || highlight === undefined) return;
    if (thread.items.some((r) => r.id === highlight.commentId)) return;
    if (thread.hasMore && !thread.isFetching) thread.fetchMore();
  }, [highlightInThread, highlight, thread]);

  const items =
    seed !== undefined && !thread.items.some((r) => r.id === seed.id)
      ? [seed, ...thread.items]
      : thread.items;
  const showThread = expanded || seeded;
  const hiddenCount = Math.max(0, replyCount - items.length);

  // Opening the reply composer loads the whole thread first (industry
  // standard: you see everything already said before adding to it — the new
  // reply appends after the hidden ones). Guarded by `expanded` so it runs
  // once per thread; seeded threads with nothing hidden skip the fetch.
  useEffect(() => {
    if (!composerOpen || expanded) return;
    if (!seeded) {
      setExpanded(true);
      return;
    }
    if (hiddenCount > 0) {
      setExpanded(true);
      thread.refetch();
    }
  }, [composerOpen, expanded, seeded, hiddenCount, thread]);

  if (replyCount === 0 && !composerOpen && items.length === 0) {
    return null;
  }

  const expand = () => {
    setExpanded(true);
    // The thread cache may hold only viewer-created replies appended while
    // collapsed (a synthetic cursor-less page, see useCreateFeedComment); a
    // fresh-enough cache would satisfy the `enabled` flip without fetching
    // the real pages, so force the fetch.
    if (seeded) thread.refetch();
  };

  return (
    <Stack
      gap="md"
      pl="lg"
      style={{
        // Indent the thread rule past the parent bubble's left edge to the
        // middle of its "Like" action (avatar column + gap + lg), per the
        // design mock.
        // eslint-disable-next-line @leanin/ui/no-inline-spacing-vars -- avatar-relative calc() offset is not expressible as a SpaceKey prop
        marginInlineStart:
          'calc(var(--avatar-sm) + var(--space-sm) + var(--space-lg))',
        borderLeft: '1px solid var(--border)',
      }}
    >
      {showThread ? items.map((reply) => renderItem(reply)) : null}
      {expanded && thread.error != null ? (
        <Body size="sm" color="destructive">
          {formatError(thread.error, 'repliesFailed')}
        </Body>
      ) : null}
      {!expanded && (seeded ? hiddenCount > 0 : replyCount > 0) ? (
        // Ghost padding is invisible — pull the label toward the rule.
        // eslint-disable-next-line @leanin/ui/no-inline-spacing-vars -- negative offset (undo the ghost Button's padding) has no SpaceKey equivalent
        <Row style={{ marginInlineStart: 'calc(-1 * var(--space-sm))' }}>
          <Button variant="ghost" size="sm" style={threadActionStyle} onClick={expand}>
            {seeded
              ? t('comments.viewMoreRepliesCount', { count: hiddenCount })
              : t('comments.viewReplies', { count: replyCount })}
          </Button>
        </Row>
      ) : null}
      {expanded && thread.isFetching && thread.items.length === 0 ? (
        <Row>
          <Button variant="ghost" size="sm" style={threadActionStyle} loading disabled>
            {seeded && hiddenCount > 0
              ? t('comments.viewMoreRepliesCount', { count: hiddenCount })
              : t('comments.viewReplies', { count: replyCount })}
          </Button>
        </Row>
      ) : null}
      {expanded && !thread.isFetching && thread.hasMore ? (
        <Row>
          <Button variant="ghost" size="sm" style={threadActionStyle} onClick={thread.fetchMore}>
            {t('comments.viewMoreReplies')}
          </Button>
        </Row>
      ) : null}
      {composer}
    </Stack>
  );
}

// Cheap pre-check for a pasted comment that is exactly one URL. Previews are
// built only for https URLs — the resolve endpoint and the create contracts
// require https — so an http link stays autolinked text with no card.
function pastedHttpsUrl(text: string): string | null {
  const trimmed = text.trim();
  try {
    return new URL(trimmed).protocol === 'https:' ? trimmed : null;
  } catch {
    return null;
  }
}

// One card per comment: comments are compact, and stacking several unfurls
// under a two-line reply reads as clutter (posts allow three).
const MAX_COMMENT_LINK_PREVIEWS = 1;

export function CommentsPanel({
  targetId,
  items,
  isLoading,
  loadError,
  hasEarlier,
  isFetchingEarlier,
  onFetchEarlier,
  viewerUserId,
  onCreate,
  resolveLinkPreview,
  onUpdate,
  onDelete,
  formatError,
  onCommentAdded,
  audienceGroupId,
  mentionPriorityMember,
  mentionsEnabled = true,
  mentionAllGroup,
  reportKind,
  composerEnabled = true,
  onReact,
  commentHref,
  replies,
  highlight,
}: PanelProps) {
  const t = useTranslations('Feed');
  const [draft, setDraft] = useState('');
  const [posting, setPosting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const submitInFlightRef = useRef(false);
  const editorHandleRef = useRef<RichTextEditorHandle>(null);
  const [pendingPreviewUrl, setPendingPreviewUrl] = useState<string | null>(null);
  const [linkPreviews, setLinkPreviews] = useState<LinkPreviewInput[]>([]);
  // Bumped when the draft is cleared so an unfurl still in flight can't
  // attach its card to the next, unrelated draft.
  const previewGenerationRef = useRef(0);

  const handlePasteText =
    resolveLinkPreview === undefined
      ? undefined
      : (text: string, ctx: { readonly linkApplied: boolean }): boolean => {
          const candidate = pastedHttpsUrl(text);
          if (candidate === null) return false;
          if (
            linkPreviews.length + (pendingPreviewUrl !== null ? 1 : 0) >=
              MAX_COMMENT_LINK_PREVIEWS ||
            linkPreviews.some((p) => p.url === candidate)
          )
            // At the cap the URL still has to land in the body: as the link
            // mark the editor just applied, or as autolinked text via the
            // native paste fallthrough.
            return ctx.linkApplied;
          // Keep the URL in the body — the editor already turned a non-empty
          // selection into a link; otherwise insert it as text like the post
          // composer — and unfurl a card for it. A loading placeholder shows
          // immediately; the unfurl can take seconds and silence reads as
          // failure.
          if (!ctx.linkApplied) editorHandleRef.current?.insertText(text);
          setPendingPreviewUrl(candidate);
          const generation = previewGenerationRef.current;
          void resolveLinkPreview(candidate)
            .then((preview) => {
              if (previewGenerationRef.current !== generation) return;
              setPendingPreviewUrl(null);
              setLinkPreviews((prev) =>
                prev.length >= MAX_COMMENT_LINK_PREVIEWS ||
                prev.some((p) => p.url === preview.url)
                  ? prev
                  : [...prev, preview],
              );
            })
            .catch(() => {
              // A rejected unfurl must still clear the pending gate, or the
              // Submit button (disabled while `pendingPreviewUrl !== null`)
              // stays stuck. The URL remains as autolinked body text; only the
              // card is dropped.
              if (previewGenerationRef.current !== generation) return;
              setPendingPreviewUrl(null);
            });
          return true;
        };

  // One reply composer is open at a time (`activeReplyThreadId`), but each
  // thread's in-progress draft and seeded @-mentions live in `replyDrafts`,
  // keyed by thread id. Switching which comment you reply to preserves what
  // you'd typed and restores it when you come back (issue #258); posting a
  // reply clears only that thread's entry.
  const [activeReplyThreadId, setActiveReplyThreadId] = useState<string | null>(
    null,
  );
  const [replyDrafts, setReplyDrafts] = useState<
    ReadonlyMap<string, ReplyDraftState>
  >(() => new Map());
  const [replyPosting, setReplyPosting] = useState(false);
  const [replyError, setReplyError] = useState<string | null>(null);
  const replyInFlightRef = useRef(false);

  const activeReplyDraft =
    activeReplyThreadId === null
      ? null
      : replyDrafts.get(activeReplyThreadId) ?? EMPTY_REPLY_DRAFT;

  const setThreadDraft = (threadId: string, draft: string) => {
    setReplyDrafts((prev) => {
      const next = new Map(prev);
      next.set(threadId, {
        ...(prev.get(threadId) ?? EMPTY_REPLY_DRAFT),
        draft,
      });
      return next;
    });
  };

  const openReply = (threadId: string, target: CommentItemView | null) => {
    setReplyError(null);
    setActiveReplyThreadId(threadId);
    // Preserve any in-progress draft for this thread; seed only a fresh entry.
    setReplyDrafts((prev) =>
      prev.has(threadId) ? prev : new Map(prev).set(threadId, EMPTY_REPLY_DRAFT),
    );
    // Replying to a reply prefills an @-mention of its author. The handle
    // resolves async; only a still-empty thread is seeded, so a draft the
    // viewer already started (here, or before switching away) is never
    // clobbered.
    if (target !== null && replies?.mentionForAuthor !== undefined) {
      void replies
        .mentionForAuthor(target)
        .then((mention) => {
          if (mention === null) return;
          setReplyDrafts((prev) => {
            const existing = prev.get(threadId) ?? EMPTY_REPLY_DRAFT;
            if (existing.draft.length > 0 || existing.mentions.length > 0) {
              return prev;
            }
            return new Map(prev).set(threadId, {
              draft: `@${mention.handle} `,
              mentions: [mention],
            });
          });
        })
        .catch(() => {
          // Prefill is sugar — a failed profile lookup just opens the
          // composer empty.
        });
    }
  };

  const submit = async () => {
    setSubmitError(null);
    const body = draft.trim();
    if (body.length === 0) {
      setSubmitError(t('comments.writeFirst'));
      return;
    }
    // A pasted URL still unfurling lives in `pendingPreviewUrl`, not yet in
    // `linkPreviews` — posting now would drop its card. Wait for it, matching
    // the feed composer's in-flight gate.
    if (pendingPreviewUrl !== null) {
      setSubmitError(t('comments.waitPreview'));
      return;
    }
    // Double-submit guard. A same-tick double Enter/click fires onSubmit
    // twice before React commits `posting` (or the disabled button) — both
    // read the stale state — so async state can't stop the second POST. A
    // ref flips synchronously and does.
    if (submitInFlightRef.current) return;
    submitInFlightRef.current = true;
    setPosting(true);
    try {
      await onCreate(body, linkPreviews);
      setDraft('');
      previewGenerationRef.current += 1;
      setPendingPreviewUrl(null);
      setLinkPreviews([]);
      onCommentAdded?.();
    } catch (err) {
      setSubmitError(formatError(err, 'postFailed'));
    } finally {
      submitInFlightRef.current = false;
      setPosting(false);
    }
  };

  const submitReply = async () => {
    if (replies === undefined || activeReplyThreadId === null) return;
    setReplyError(null);
    const threadId = activeReplyThreadId;
    const body = (replyDrafts.get(threadId)?.draft ?? '').trim();
    if (body.length === 0) {
      setReplyError(t('comments.writeFirst'));
      return;
    }
    if (replyInFlightRef.current) return;
    replyInFlightRef.current = true;
    setReplyPosting(true);
    try {
      await replies.onCreateReply(threadId, body);
      // Clear only this thread's draft and close its composer.
      setReplyDrafts((prev) => {
        if (!prev.has(threadId)) return prev;
        const next = new Map(prev);
        next.delete(threadId);
        return next;
      });
      setActiveReplyThreadId(null);
      onCommentAdded?.();
    } catch (err) {
      setReplyError(formatError(err, 'postFailed'));
    } finally {
      replyInFlightRef.current = false;
      setReplyPosting(false);
    }
  };

  const replyComposer =
    activeReplyThreadId === null || activeReplyDraft === null ? null : (
      <Stack gap="sm">
        <Row gap="sm" align="flex-end">
          <CommentComposer
            key={`reply-${activeReplyThreadId}-${activeReplyDraft.mentions.length}`}
            id={`comment-reply-${activeReplyThreadId}`}
            value={activeReplyDraft.draft}
            onChange={(next) => setThreadDraft(activeReplyThreadId, next)}
            onSubmit={() => void submitReply()}
            placeholder={t('comments.replyPlaceholder')}
            ariaLabel={t('comments.replyLabel')}
            disabled={replyPosting}
            mentionsEnabled={mentionsEnabled}
            initialMentions={activeReplyDraft.mentions}
            {...(audienceGroupId !== undefined ? { audienceGroupId } : {})}
            {...(mentionPriorityMember !== undefined
              ? { mentionPriorityMember }
              : {})}
            {...(mentionAllGroup !== undefined ? { mentionAllGroup } : {})}
          />
          <Button
            type="button"
            variant="primary"
            size="sm"
            onClick={() => void submitReply()}
            disabled={activeReplyDraft.draft.trim().length === 0}
            loading={replyPosting}
          >
            {t('comments.submit')}
          </Button>
        </Row>
        {replyError !== null ? (
          <Body size="sm" color="destructive">
            {replyError}
          </Body>
        ) : null}
      </Stack>
    );

  const itemPlumbing = {
    viewerUserId,
    onUpdate,
    onDelete,
    formatError,
    audienceGroupId,
    mentionPriorityMember,
    mentionsEnabled,
    mentionAllGroup,
    reportKind,
    onReact,
    commentHref,
  };

  return (
    <Stack gap="lg" aria-label={t('comments.regionAria')}>
      {isLoading ? null : loadError != null ? (
        <Body color="destructive">{formatError(loadError, 'loadFailed')}</Body>
      ) : items.length === 0 ? null : (
        // Comments are list rows, so they sit on the md tier, and a thread
        // hugs its parent one tier tighter (sm) — otherwise a reply reads as
        // far from the comment it answers as the next comment does.
        <Stack gap="md">
          <LoadMore
            hasMore={hasEarlier}
            isLoading={isFetchingEarlier}
            onClick={onFetchEarlier}
            size="sm"
            label={t('comments.viewEarlier')}
          />
          {items.map((c) => (
            <Stack key={c.id} gap="sm">
              <CommentItem
                comment={c}
                {...itemPlumbing}
                {...(replies !== undefined && composerEnabled
                  ? { onReplyClick: () => openReply(c.id, null) }
                  : {})}
                highlighted={highlight?.commentId === c.id}
              />
              {replies !== undefined ? (
                <ReplyThread
                  parent={c}
                  replies={replies}
                  highlight={highlight}
                  composer={
                    activeReplyThreadId === c.id ? replyComposer : null
                  }
                  formatError={formatError}
                  renderItem={(reply) => (
                    <CommentItem
                      key={reply.id}
                      comment={reply}
                      {...itemPlumbing}
                      {...(composerEnabled
                        ? { onReplyClick: () => openReply(c.id, reply) }
                        : {})}
                      highlighted={highlight?.commentId === reply.id}
                    />
                  )}
                />
              ) : null}
            </Stack>
          ))}
        </Stack>
      )}
      {composerEnabled ? (
        <>
          <Row gap="sm" align="flex-end">
            <CommentComposer
              id={`comment-${targetId}`}
              value={draft}
              onChange={setDraft}
              onSubmit={() => void submit()}
              placeholder={t('comments.addPlaceholder')}
              disabled={posting}
              mentionsEnabled={mentionsEnabled}
              handle={editorHandleRef}
              {...(handlePasteText !== undefined
                ? { onPasteText: handlePasteText }
                : {})}
              {...(audienceGroupId !== undefined ? { audienceGroupId } : {})}
              {...(mentionPriorityMember !== undefined
                ? { mentionPriorityMember }
                : {})}
              {...(mentionAllGroup !== undefined ? { mentionAllGroup } : {})}
            />
            <Button
              type="button"
              variant="primary"
              onClick={() => void submit()}
              disabled={draft.trim().length === 0 || pendingPreviewUrl !== null}
              loading={posting}
            >
              {t('comments.submit')}
            </Button>
          </Row>
          {pendingPreviewUrl !== null || linkPreviews.length > 0 ? (
            <div style={{ maxWidth: 480 }}>
              <Stack gap="sm">
                {linkPreviews.map((preview) => (
                  <LinkPreviewCard.Editable
                    key={preview.url}
                    preview={preview}
                    onRemove={() =>
                      setLinkPreviews((prev) =>
                        prev.filter((p) => p.url !== preview.url),
                      )
                    }
                  />
                ))}
                {pendingPreviewUrl !== null ? (
                  <LinkPreviewCard.Loading url={pendingPreviewUrl} />
                ) : null}
              </Stack>
            </div>
          ) : null}
        </>
      ) : null}
      {submitError !== null ? (
        <Body color="destructive">
          {submitError}
        </Body>
      ) : null}
    </Stack>
  );
}
